import type {
  ACSOutgoingMessages,
  ErrorCode,
  Profile,
  Subscription,
} from "@mos-utils/acs-core";
import acs from "./acs";

type State =
  | {
      value: "unknown";
      context: {
        profile: null;
        profileError: null;
      };
    }
  | {
      value: "authorized";
      context: {
        profile: null | Profile;
        profileError: null | ErrorCode;
      };
    }
  | {
      value: "unauthorized";
      context: {
        profile: null;
        profileError: null;
      };
    }
  | {
      value: "exit";
      context: {
        profile: null | Profile;
        profileError: null | ErrorCode;
      };
    };

const initialState: State = {
  value: "unknown",
  context: {
    profile: null,
    profileError: null,
  },
};

const reducer = function (state: State, message: ACSOutgoingMessages): State {
  if (message.type === "EXITING") return { ...state, value: "exit" };
  if (state.value === "unknown") {
    if (message.type === "USER_AUTHORIZED") {
      return { ...state, value: "authorized" };
    }
    if (message.type === "USER_UNAUTHORIZED") {
      return { ...state, value: "unauthorized" };
    }
  }
  if (state.value === "authorized") {
    if (message.type === "PROFILE_UPDATED") {
      return {
        ...state,
        context: { ...state.context, profile: message.profile },
      };
    }
    if (message.type === "PROFILE_ERROR") {
      return {
        ...state,
        context: { ...state.context, profileError: message.code },
      };
    }
  }
  return state;
};

let state = reducer(initialState, {
  type: "INIT",
} as unknown as ACSOutgoingMessages);

acs.subscribe((message) => {
  state = Array.isArray(message)
    ? message.reduce(reducer, state)
    : reducer(state, message);
});

/**
 * Возвращает текущий статус авторизации и профиль пользователя, если есть
 * @public
 * @deprecated
 * Если вы используете React, воспользуйтесь пакетом `@mos-utils/acs-react`.
 * Иначе, подпишитесь на сообщения от ядра с помощью `acs.subscribe()`
 */
export function getState(): State {
  return state;
}

/**
 * Уводит пользователя на страницу логина
 * @param backUrl - Позволяет перепистаь `backUrl`. Переданное здесь значение имеет наивысший приоритет
 * @public
 * @deprecated Используйте `acs.login()`
 */
export function login(backUrl?: string): void {
  acs.login(backUrl);
}

/**
 * Уводит пользователя на страницу логаута
 * @param backUrl - Позволяет перепистаь `backUrl`. Переданное здесь значение имеет наивысший приоритет
 * @public
 * @deprecated Используйте `acs.logout()`
 */
export function logout(backUrl?: string): void {
  acs.logout(backUrl);
}

/**
 * @public
 * @deprecated
 * Для получения статуса авторизации, подпишитесь на сообщения ядра с помощью `acs.subscribe()`.
 * Ожидайте сообщений с типами `USER_AUTHORIZED` и `USER_UNAUTHORIZED`
 */
export function probe(): Promise<boolean> {
  let unsubscribe: ReturnType<(typeof acs)["subscribe"]>;
  return new Promise<boolean>((resolve) => {
    let exiting = false;
    const subscription: Subscription = function listener(message) {
      if (exiting) return;
      if (Array.isArray(message)) {
        const message_types = new Set(message.map(({ type }) => type));
        if (message_types.has("EXITING")) {
          exiting = true;
          return;
        }
        if (message_types.has("USER_AUTHORIZED")) return resolve(true);
        if (message_types.has("USER_UNAUTHORIZED")) return resolve(false);
        return;
      }
      if (message.type === "EXITING") {
        exiting = true;
        return;
      }
      if (message.type === "USER_AUTHORIZED") return resolve(true);
      if (message.type === "USER_UNAUTHORIZED") return resolve(false);
    };
    unsubscribe = acs.subscribe(subscription);
  }).then((is_authorized) => {
    unsubscribe?.();
    return is_authorized;
  });
}

/**
 * @public
 * @deprecated Подпишитесь на сообщения ядра с помощью `acs.subscribe`,
 * ожидадйте сообщений с типами `USER_UNAUTHORIZED` и `TOKEN_REFRESH_SUCCESSFUL`.
 * Используйте `acs.refreshToken()` для отправки запроса обновления токена.
 */
export function getToken(): Promise<boolean> {
  let unsubscribe: ReturnType<(typeof acs)["subscribe"]>;
  return new Promise<boolean>((resolve) => {
    let exiting = false;
    const subscription: Subscription = function listener(message) {
      if (exiting) return;
      if (Array.isArray(message)) {
        const message_types = new Set(message.map(({ type }) => type));
        if (message_types.has("EXITING")) {
          exiting = true;
          return;
        }
        if (message_types.has("USER_UNAUTHORIZED")) return resolve(false);
        return;
      }
      if (message.type === "EXITING") {
        exiting = true;
        return;
      }
      if (message.type === "TOKEN_REFRESH_SUCCESS") return resolve(true);
      if (message.type === "USER_UNAUTHORIZED") return resolve(false);
    };
    unsubscribe = acs.subscribe(subscription);
  }).then((is_authorized) => {
    unsubscribe?.();
    return is_authorized;
  });
}

/**
 * Позволяет сконфигурировать некоторые аспекты поведения acs, такие как текущий мок и backUrl
 * @public
 * @deprecated Используйте `acs.configure()` для конфигурации backURL
 */
export function configure(options: { backUrl: string }): void {
  acs.configure(options);
}

/** @public
 * @deprecated
 * Подпишитесь на сообщения ядра с помощью `acs.subscribe`
 * и слушайте сообщение с типом `PROFILE_UPDATED`
 */
export function getProfile(): Promise<Profile> {
  const unauthorizedError: {
    code: string;
    message: string;
    errors: string[];
  } = {
    code: "USER_UNAUTHORIZED",
    message: "Невозможно получить профиль неавторизованного пользователя",
    errors: [],
  };
  let unsubscribe: ReturnType<(typeof acs)["subscribe"]>;
  return new Promise<Profile>((resolve, reject) => {
    let exiting = false;
    const subscription: Subscription = function (message) {
      if (exiting) return;
      if (Array.isArray(message)) {
        const message_types = new Set(message.map(({ type }) => type));
        if (message_types.has("EXITING")) {
          exiting = true;
          return;
        }
        if (message_types.has("USER_UNAUTHORIZED")) {
          reject(unauthorizedError);
        }
        return;
      }
      if (message.type === "EXITING") {
        exiting = true;
        return;
      }
      if (message.type === "PROFILE_ERROR") {
        reject(message);
      }
      if (message.type === "PROFILE_UPDATED") {
        resolve(message.profile);
      }
    };
    unsubscribe = acs.subscribe(subscription);
    acs.refetchProfile();
  })
    .then((profile) => {
      unsubscribe?.();
      return profile;
    })
    .catch((error) => {
      unsubscribe?.();
      return Promise.reject(error);
    });
}
